// Item E — shared seasonality / intra-year shape catalog for the AI forecast
// entry points. Ids + math mirror backend/Backend-Flask/forecast_insights.py
// (SEASONALITY_PROFILES, seasonal_shape_weights). The backend computes the REAL
// applied weights from data; these illustrative weights only draw the sparklines
// that let a user "see" each shape before running. Annual totals are always
// preserved server-side — these are normalized previews, not the figures.

export const DEFAULT_SEASONALITY = { profile: 'auto', intraQuarter: 'even', custom: { quarters: [25, 25, 25, 25] } };

export const SEASONALITY_OPTIONS = [
  { id: 'auto', label: 'Auto', hint: 'Use the line’s own seasonal pattern (recommended). Spreads ~flat when no seasonality is detected.' },
  { id: 'flat', label: 'Even', hint: 'Spread the annual amount evenly across all months.' },
  { id: 'front', label: 'Front-load', hint: 'Weight the first half of the year more heavily.' },
  { id: 'back', label: 'Back-load', hint: 'Weight the second half of the year more heavily.' },
  { id: 'prior_year', label: 'Prior-year', hint: 'Repeat the most recent 12 months’ calendar shape.' },
  { id: 'tie_to_driver', label: 'Tie to driver', hint: 'Follow the linked driver line’s shape (e.g. fees follow deposits).' },
  { id: 'custom', label: 'Custom %', hint: 'Allocate the year by quarter (Q1–Q4 must total ~100%).' }
];

export const INTRA_QUARTER_OPTIONS = [
  { id: 'even', label: 'Even quarter', hint: 'Spread evenly within each quarter.' },
  { id: 'quarter_end', label: 'Quarter-end', hint: 'Weight the last month of each quarter (booking push).' },
  { id: 'quarter_start', label: 'Quarter-start', hint: 'Weight the first month of each quarter.' }
];

// Profiles that map cleanly to the existing balance-lever quarterly presets
// (forecastQuarterlyGrowth.js SEASONALITY_PRESETS) for the Ask AI box accept path.
export const PROFILE_TO_QUARTER_PRESET = { flat: 'even', auto: 'even', front: 'front', back: 'back' };

// Intra-quarter month weighting (months 1-3 of each quarter). Single source of
// truth shared by applyIntraQuarter and the preview sparklines; 'even' is a no-op.
export const INTRA_QUARTER_FACTORS = {
  even: [1, 1, 1], quarter_end: [0.7, 1.0, 1.3], quarter_start: [1.3, 1.0, 0.7]
};

const ramp = (dir) => {
  const slope = 0.7; const mid = 5.5;
  const raw = Array.from({ length: 12 }, (_, i) => 1 + slope * ((mid - i) / mid));
  return dir === 'back' ? raw.reverse() : raw;
};

// Illustrative-only waves for shapes whose real weights depend on data.
const AUTO_WAVE = [0.85, 0.9, 1.0, 1.05, 1.0, 0.92, 0.9, 0.98, 1.08, 1.12, 1.25, 1.05];
const PRIOR_WAVE = [0.9, 1.05, 1.15, 1.0, 0.9, 0.95, 1.05, 1.1, 0.95, 0.9, 1.0, 1.05];
const DRIVER_WAVE = [0.7, 0.85, 1.0, 1.15, 1.25, 1.2, 1.1, 1.0, 0.95, 0.9, 0.85, 1.05];

const normalize = (weights) => {
  const cleaned = weights.map((w) => Math.max(0, Number(w) || 0));
  const total = cleaned.reduce((s, w) => s + w, 0);
  if (cleaned.length !== 12 || total <= 0) return Array.from({ length: 12 }, () => 1 / 12);
  return cleaned.map((w) => w / total);
};

const applyIntraQuarter = (weights, mode) => {
  if (!mode || mode === 'even') return weights;
  const factors = INTRA_QUARTER_FACTORS[mode];
  if (!factors) return weights;
  const out = [];
  for (let q = 0; q < 4; q += 1) {
    const quarter = weights.slice(q * 3, q * 3 + 3);
    const qTotal = quarter.reduce((s, w) => s + w, 0);
    const scaled = quarter.map((w, i) => w * factors[i]);
    const sTotal = scaled.reduce((s, w) => s + w, 0) || 1;
    scaled.forEach((s) => out.push((s / sTotal) * qTotal));
  }
  return out;
};

const customWeights = (custom) => {
  const quarters = custom?.quarters;
  if (Array.isArray(quarters) && quarters.length === 4) {
    const expanded = [];
    quarters.forEach((q) => {
      const per = Math.max(0, Number(q) || 0) / 3;
      expanded.push(per, per, per);
    });
    return normalize(expanded);
  }
  return Array.from({ length: 12 }, () => 1 / 12);
};

// 12 normalized weights for a sparkline preview (display only).
export const illustrativeWeights = ({ profile = 'auto', intraQuarter = 'even', custom } = {}) => {
  let base;
  switch (profile) {
    case 'flat': base = Array.from({ length: 12 }, () => 1); break;
    case 'front': base = ramp('front'); break;
    case 'back': base = ramp('back'); break;
    case 'prior_year': base = PRIOR_WAVE; break;
    case 'tie_to_driver': base = DRIVER_WAVE; break;
    case 'custom': base = customWeights(custom); break;
    case 'auto':
    default: base = AUTO_WAVE; break;
  }
  return normalize(applyIntraQuarter(normalize(base), intraQuarter));
};

export const describeSeasonality = (value = DEFAULT_SEASONALITY) => {
  const opt = SEASONALITY_OPTIONS.find((o) => o.id === value.profile) || SEASONALITY_OPTIONS[0];
  const iq = INTRA_QUARTER_OPTIONS.find((o) => o.id === value.intraQuarter);
  return iq && iq.id !== 'even' ? `${opt.label} · ${iq.label}` : opt.label;
};

export const customQuartersTotal = (custom) =>
  (custom?.quarters || []).reduce((s, q) => s + (Number(q) || 0), 0);
